import React, { useMemo } from 'react';
import { useTable, Column } from 'react-table';
import {
  HTMLTable,
  HTMLTableProps as IHTMLTableProps
} from '@blueprintjs/core';

const GetStyle = (
  defaultStyle: React.CSSProperties,
  style: React.CSSProperties
) => {
  if (!style) {
    return defaultStyle;
  }
  return Object.assign({}, defaultStyle, style);
};

const GetClassName = (defaultClassName: string, className: string) => {
  if (!className) {
    return defaultClassName;
  }
  if (defaultClassName) {
    return `${defaultClassName} ${className}`;
  }
  return className;
};

const GetStyleAndClasses = (
  defaultStyle: React.CSSProperties,
  style: React.CSSProperties,
  defaultClassName: string,
  className: string
) => {
  return {
    style: GetStyle(defaultStyle, style),
    className: GetClassName(defaultClassName, className)
  };
};

const Extract = <T extends object>(
  getter: (data?: T) => React.CSSProperties | string,
  data: T
) => {
  if (!getter) {
    return;
  }
  return getter(data);
};

export interface TableProps<T extends object> extends IHTMLTableProps {
  columns: Array<Column<T>>;
  data: Array<T>;
  dependencies?: any[];
  getRowStyle?: (data?: T) => React.CSSProperties;
  getRowClass?: (data?: T) => string;
  getCellStyle?: (data?: T) => string;
  getCellClass?: (data?: T) => string;
  style?: any;
}

const Table = <T extends object>({
  columns: _columns,
  data: _data,
  dependencies,
  getRowStyle,
  getRowClass,
  getCellStyle,
  getCellClass,
  interactive,
  bordered,
  striped,
  style
}: TableProps<T>): JSX.Element => {
  dependencies = dependencies || [];
  const columns = useMemo(() => _columns, [...dependencies]);
  const data = useMemo(() => _data, [_data]);
  const { headerGroups, rows, prepareRow } = useTable({ columns, data });
  return (
    <HTMLTable
      className="w-full"
      style={style}
      bordered={bordered}
      striped={striped}
      interactive={interactive}
    >
      <thead>
        {headerGroups.map((headerGroup) => {
          const headerGroupProps = headerGroup.getHeaderGroupProps();
          return (
            <tr key={headerGroupProps.key} {...headerGroupProps}>
              {headerGroup.headers.map((column) => {
                const headerProps = column.getHeaderProps();
                return (
                  <th key={headerProps.key} {...headerProps}>
                    {column.render('Header')}
                  </th>
                );
              })}
            </tr>
          );
        })}
      </thead>
      <tbody>
        {rows.map((row) => {
          prepareRow(row);
          const rowProps = row.getRowProps();
          const rowStyle = Extract(
            getRowStyle,
            row.original
          ) as React.CSSProperties;
          const rowClassName = Extract(getRowClass, row.original) as string;
          const styles = GetStyleAndClasses(
            rowProps.style,
            rowStyle,
            rowProps.className,
            rowClassName
          );
          return (
            <tr
              key={rowProps.key}
              {...rowProps}
              className={styles.className}
              style={styles.style}
            >
              {row.cells.map((cell) => {
                const cellProps = cell.getCellProps();
                const cellStyle = Extract(
                  getCellStyle,
                  cell.row.original
                ) as React.CSSProperties;
                const cellClassName = Extract(
                  getCellClass,
                  cell.row.original
                ) as string;
                const cellStyles = GetStyleAndClasses(
                  cellProps.style,
                  cellStyle,
                  cellProps.className,
                  cellClassName
                );
                return (
                  <td
                    key={cellProps.key}
                    {...cellProps}
                    className={cellStyles.className}
                    style={cellStyles.style}
                  >
                    {cell.render('Cell')}
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </HTMLTable>
  );
};

export default Table;
